import logger from "../utils/logger";
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Check, ChevronRight, User, FileText, ClipboardList, PartyPopper, Upload } from 'lucide-react';
import { toast } from 'sonner';

import API_URL from '../utils/api';

const STEPS = [
    { id: 'profile', label: 'Complete Profile', icon: User },
    { id: 'documents', label: 'Upload Documents', icon: FileText },
    { id: 'checklist', label: 'Onboarding Tasks', icon: ClipboardList },
    { id: 'done', label: 'Welcome!', icon: PartyPopper },
];

const SelfOnboarding = () => {
    const { user, checkAuth } = useAuth();
    const navigate = useNavigate();
    const fileRef = useRef(null);
    const [step, setStep] = useState(0);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [profile, setProfile] = useState({
        phone: '', address: '', city: '', state: '', date_of_birth: '', gender: '',
        emergency_contact_name: '', emergency_contact_phone: '', blood_group: '',
        bank_account_number: '', bank_name: '', bank_ifsc: ''
    });
    const [documents, setDocuments] = useState([]);
    const [docType, setDocType] = useState('id_proof');
    const [uploading, setUploading] = useState(false);

    useEffect(() => { fetchStatus(); }, []);

    const fetchStatus = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/self-onboarding/status`, { withCredentials: true });
            setStatus(data);
            if (data.profile) setProfile(prev => ({ ...prev, ...Object.fromEntries(Object.entries(data.profile).filter(([_, v]) => v)) }));
            // Auto-advance step
            if (data.profile_completed) {
                if (data.documents_uploaded) {
                    if (data.checklist_done || data.checklist_total === 0) setStep(3);
                    else setStep(2);
                } else setStep(1);
            }
            // Fetch documents
            const docsRes = await axios.get(`${API_URL}/api/employees/${user?._id || user?.id}/documents`, { withCredentials: true });
            setDocuments(docsRes.data);
        } catch (error) { logger.error(error); }
        finally { setLoading(false); }
    };

    const handleProfileSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.put(`${API_URL}/api/self-onboarding/profile`, profile, { withCredentials: true });
            await fetchStatus();
            setStep(1);
        } catch (error) { toast.error(error.response?.data?.detail || 'Error saving profile'); }
        finally { setSaving(false); }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            await axios.post(`${API_URL}/api/employees/${user?._id || user?.id}/documents?doc_type=${docType}`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            await fetchStatus();
            const docsRes = await axios.get(`${API_URL}/api/employees/${user?._id || user?.id}/documents`, { withCredentials: true });
            setDocuments(docsRes.data);
        } catch (error) { toast.error(error.response?.data?.detail || 'Upload failed'); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
    };

    const toggleChecklistItem = async (index, completed) => {
        try {
            await axios.put(`${API_URL}/api/onboarding/${status.onboarding_record._id}/item`, { item_index: index, completed: !completed }, { withCredentials: true });
            await fetchStatus();
        } catch (error) { toast.error(error.response?.data?.detail || 'Error updating'); }
    };

    const handleComplete = async () => {
        setSaving(true);
        try {
            await axios.post(`${API_URL}/api/self-onboarding/complete`, {}, { withCredentials: true });
            await checkAuth();
            navigate('/dashboard');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error completing onboarding'); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-slate-500">Loading...</div></div>;

    const DOC_TYPES = [
        { value: 'aadhar', label: 'Aadhar Card' },
        { value: 'pan', label: 'PAN Card' },
        { value: 'passport', label: 'Passport' },
        { value: 'voter_id', label: 'Voter ID' },
        { value: 'address_proof', label: 'Address Proof' },
        { value: 'resume', label: 'Resume / CV' },
        { value: 'education_10th', label: '10th Marksheet' },
        { value: 'education_12th', label: '12th Marksheet' },
        { value: 'education_degree', label: 'Degree Certificate' },
        { value: 'experience_letter', label: 'Experience Letter' },
        { value: 'bank_proof', label: 'Bank Passbook / Statement' },
        { value: 'photo', label: 'Passport Photo' },
        { value: 'contract', label: 'Signed Contract' },
        { value: 'other', label: 'Other' },
    ];

    return (
        <div className="min-h-screen bg-slate-50" data-testid="self-onboarding-page">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-6 py-4">
                <div className="max-w-3xl mx-auto flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Welcome to DIGI-LOANS</h1>
                        <p className="text-sm text-slate-500">Hi {user?.name}, let's get you set up!</p>
                    </div>
                    {step < 3 && <Button variant="ghost" size="sm" className="text-xs text-slate-400" onClick={() => { handleComplete(); }} data-testid="skip-onboarding-btn">Skip for now</Button>}
                </div>
            </div>

            {/* Stepper */}
            <div className="max-w-3xl mx-auto px-6 py-6">
                <div className="flex items-center gap-0 mb-8">
                    {STEPS.map((s, i) => (
                        <div key={s.id} className="flex items-center flex-1">
                            <div className={`flex items-center gap-2 ${i <= step ? 'text-brand' : 'text-slate-400'}`}>
                                <div className={`w-8 h-8 flex items-center justify-center text-sm font-bold border-2 transition-all ${
                                    i < step ? 'bg-brand border-brand text-white' : i === step ? 'border-brand text-brand' : 'border-slate-300 text-slate-400'
                                }`} data-testid={`step-indicator-${s.id}`}>
                                    {i < step ? <Check className="h-4 w-4" /> : i + 1}
                                </div>
                                <span className="text-xs font-medium hidden sm:block">{s.label}</span>
                            </div>
                            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? 'bg-brand' : 'bg-slate-200'}`} />}
                        </div>
                    ))}
                </div>

                {/* Step 1: Profile */}
                {step === 0 && (
                    <Card className="rounded-none border-slate-200" data-testid="step-profile">
                        <CardContent className="p-6">
                            <h2 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Complete Your Profile</h2>
                            <p className="text-sm text-slate-500 mb-6">Fill in your personal details so HR has everything they need.</p>
                            <form onSubmit={handleProfileSave} className="space-y-5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</Label>
                                        <Input value={profile.phone} onChange={(e) => setProfile({...profile, phone: e.target.value})} className="rounded-none mt-1" placeholder="e.g. +91 9876543210" data-testid="profile-phone" /></div>
                                    <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Date of Birth</Label>
                                        <Input type="date" value={profile.date_of_birth} onChange={(e) => setProfile({...profile, date_of_birth: e.target.value})} className="rounded-none mt-1" data-testid="profile-dob" /></div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Gender</Label>
                                        <Select value={profile.gender || "not_set"} onValueChange={(v) => setProfile({...profile, gender: v === "not_set" ? "" : v})}>
                                            <SelectTrigger className="rounded-none mt-1" data-testid="profile-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                                            <SelectContent className="rounded-none">
                                                <SelectItem value="not_set">Select Gender</SelectItem>
                                                <SelectItem value="male">Male</SelectItem>
                                                <SelectItem value="female">Female</SelectItem>
                                                <SelectItem value="other">Other</SelectItem>
                                            </SelectContent>
                                        </Select></div>
                                    <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Blood Group</Label>
                                        <Select value={profile.blood_group || "not_set"} onValueChange={(v) => setProfile({...profile, blood_group: v === "not_set" ? "" : v})}>
                                            <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                                            <SelectContent className="rounded-none">
                                                <SelectItem value="not_set">Select</SelectItem>
                                                {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
                                            </SelectContent>
                                        </Select></div>
                                </div>
                                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Address</Label>
                                    <Input value={profile.address} onChange={(e) => setProfile({...profile, address: e.target.value})} className="rounded-none mt-1" placeholder="Street address" data-testid="profile-address" /></div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">City</Label>
                                        <Input value={profile.city} onChange={(e) => setProfile({...profile, city: e.target.value})} className="rounded-none mt-1" /></div>
                                    <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">State</Label>
                                        <Input value={profile.state} onChange={(e) => setProfile({...profile, state: e.target.value})} className="rounded-none mt-1" /></div>
                                </div>

                                <div className="pt-2 border-t border-slate-200">
                                    <h3 className="text-sm font-bold text-slate-700 mb-3">Emergency Contact</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Contact Name</Label>
                                            <Input value={profile.emergency_contact_name} onChange={(e) => setProfile({...profile, emergency_contact_name: e.target.value})} className="rounded-none mt-1" data-testid="profile-emergency-name" /></div>
                                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Contact Phone</Label>
                                            <Input value={profile.emergency_contact_phone} onChange={(e) => setProfile({...profile, emergency_contact_phone: e.target.value})} className="rounded-none mt-1" data-testid="profile-emergency-phone" /></div>
                                    </div>
                                </div>

                                <div className="pt-2 border-t border-slate-200">
                                    <h3 className="text-sm font-bold text-slate-700 mb-3">Bank Details</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Bank Name</Label>
                                            <Input value={profile.bank_name} onChange={(e) => setProfile({...profile, bank_name: e.target.value})} className="rounded-none mt-1" data-testid="profile-bank-name" /></div>
                                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Account Number</Label>
                                            <Input value={profile.bank_account_number} onChange={(e) => setProfile({...profile, bank_account_number: e.target.value})} className="rounded-none mt-1" /></div>
                                        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">IFSC Code</Label>
                                            <Input value={profile.bank_ifsc} onChange={(e) => setProfile({...profile, bank_ifsc: e.target.value})} className="rounded-none mt-1" /></div>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <Button type="submit" className="bg-brand hover:bg-brand rounded-none px-8" disabled={saving} data-testid="save-profile-btn">
                                        {saving ? 'Saving...' : 'Save & Continue'} <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                )}

                {/* Step 2: Documents */}
                {step === 1 && (
                    <Card className="rounded-none border-slate-200" data-testid="step-documents">
                        <CardContent className="p-6">
                            <h2 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Upload Your Documents</h2>
                            <p className="text-sm text-slate-500 mb-6">Upload required documents like ID proof, address proof, and certificates.</p>

                            <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 border border-slate-200">
                                <Select value={docType} onValueChange={setDocType}>
                                    <SelectTrigger className="w-[200px] rounded-none" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                                    <SelectContent className="rounded-none">
                                        {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-brand hover:bg-brand rounded-none" data-testid="upload-btn">
                                    <Upload className="h-4 w-4 mr-2" />{uploading ? 'Uploading...' : 'Choose File'}
                                </Button>
                                <input ref={fileRef} type="file" className="hidden" onChange={handleFileUpload} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt" />
                            </div>

                            {documents.length > 0 && (
                                <div className="border border-slate-200 divide-y divide-slate-100 mb-6">
                                    {documents.map(doc => (
                                        <div key={doc._id} className="flex items-center gap-3 px-4 py-3" data-testid={`uploaded-doc-${doc._id}`}>
                                            <FileText className="h-4 w-4 text-brand flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-slate-900 truncate">{doc.original_filename}</p>
                                                <p className="text-[10px] text-slate-400">{DOC_TYPES.find(t => t.value === doc.doc_type)?.label || doc.doc_type}</p>
                                            </div>
                                            <Badge className="rounded-none bg-green-100 text-green-700 text-[10px]">Uploaded</Badge>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {documents.length === 0 && (
                                <div className="py-8 text-center text-slate-500 border border-dashed border-slate-300 mb-6">
                                    <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm">No documents uploaded yet</p>
                                    <p className="text-xs text-slate-400 mt-1">Upload at least one document to continue</p>
                                </div>
                            )}

                            <div className="flex justify-between pt-4">
                                <Button variant="outline" className="rounded-none" onClick={() => setStep(0)} data-testid="back-to-profile-btn">Back</Button>
                                <Button className="bg-brand hover:bg-brand rounded-none px-8" onClick={() => setStep(2)} data-testid="continue-to-checklist-btn">
                                    Continue <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Step 3: Checklist */}
                {step === 2 && (
                    <Card className="rounded-none border-slate-200" data-testid="step-checklist">
                        <CardContent className="p-6">
                            <h2 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Onboarding Tasks</h2>
                            <p className="text-sm text-slate-500 mb-6">Complete these tasks to finish your onboarding process.</p>

                            {status?.onboarding_record ? (
                                <>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                                            <div className="h-full bg-brand transition-all rounded-full" style={{ width: `${status.onboarding_record.progress || 0}%` }} />
                                        </div>
                                        <span className="text-sm font-bold text-slate-700">{status.checklist_progress}/{status.checklist_total}</span>
                                    </div>
                                    <div className="space-y-1 mb-6">
                                        {status.onboarding_record.checklist?.map((item, i) => (
                                            <button key={i} onClick={() => toggleChecklistItem(i, item.completed)}
                                                className={`flex items-center gap-3 w-full text-left px-4 py-3 border border-slate-200 transition-colors ${item.completed ? 'bg-green-50' : 'hover:bg-slate-50'}`}
                                                data-testid={`onboarding-item-${i}`}>
                                                <div className={`w-5 h-5 flex items-center justify-center border-2 transition-colors flex-shrink-0 ${item.completed ? 'bg-green-600 border-green-600' : 'border-slate-300'}`}>
                                                    {item.completed && <Check className="h-3 w-3 text-white" />}
                                                </div>
                                                <span className={`text-sm flex-1 ${item.completed ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{item.item}</span>
                                                {item.completed && <Badge className="rounded-none bg-green-100 text-green-700 text-[10px]">Done</Badge>}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="py-8 text-center text-slate-500 mb-6">
                                    <ClipboardList className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm">No checklist assigned yet</p>
                                    <p className="text-xs text-slate-400 mt-1">Your HR team will assign tasks soon. You can continue for now.</p>
                                </div>
                            )}

                            <div className="flex justify-between pt-4">
                                <Button variant="outline" className="rounded-none" onClick={() => setStep(1)}>Back</Button>
                                <Button className="bg-brand hover:bg-brand rounded-none px-8" onClick={() => setStep(3)} data-testid="continue-to-finish-btn">
                                    Continue <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Step 4: Welcome */}
                {step === 3 && (
                    <Card className="rounded-none border-slate-200" data-testid="step-done">
                        <CardContent className="p-8 text-center">
                            <div className="w-16 h-16 bg-green-100 flex items-center justify-center mx-auto mb-4">
                                <Check className="h-8 w-8 text-green-600" />
                            </div>
                            <h2 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>You're All Set!</h2>
                            <p className="text-slate-500 mb-6 max-w-md mx-auto">Your onboarding is complete. Welcome to the team, {user?.name}! You can now access all your tools and start working.</p>

                            <div className="flex flex-wrap justify-center gap-3 mb-8">
                                {status?.profile_completed && <Badge className="rounded-none bg-green-100 text-green-700"><Check className="h-3 w-3 mr-1 inline" />Profile Complete</Badge>}
                                {status?.documents_uploaded && <Badge className="rounded-none bg-green-100 text-green-700"><Check className="h-3 w-3 mr-1 inline" />{status.document_count} Documents</Badge>}
                                {status?.checklist_done && <Badge className="rounded-none bg-green-100 text-green-700"><Check className="h-3 w-3 mr-1 inline" />Checklist Done</Badge>}
                            </div>

                            <Button className="bg-brand hover:bg-brand rounded-none px-10 py-3 text-base" onClick={handleComplete} disabled={saving} data-testid="go-to-dashboard-btn">
                                {saving ? 'Please wait...' : 'Go to Dashboard'}
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default SelfOnboarding;
